import { FC, useState, useEffect, useMemo } from 'react';
import { keepPreviousData } from '@tanstack/react-query';
import { Button } from '@/components/ui/button';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetFooter } from '@/components/ui/sheet';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Autocomplete, type AutocompleteOption } from '@/components/ui/auto-complete';
import { useCityFees } from 'src/hooks/useCityFees';
import { CityFee } from '@libs/schemas';
import { trpc } from 'src/trpc';
import { AlertCircle } from 'lucide-react';
import { NumericFormat } from 'react-number-format';

interface CityFeeSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  cityFee?: CityFee | null;
}

export const CityFeeSheet: FC<CityFeeSheetProps> = ({ open, onOpenChange, cityFee }) => {
  const { createFee, updateFee, isCreating, isUpdating } = useCityFees();

  // State for server-side search
  const [citySearchTerm, setCitySearchTerm] = useState('');
  const [cityPopoverOpen, setCityPopoverOpen] = useState(false);

  // Determine when to query server:
  // Query when popover is open AND (search term >= 2 chars OR initial load with empty search)
  const shouldQueryServer =
    open && cityPopoverOpen && (citySearchTerm.length >= 2 || citySearchTerm === '');

  // Server-side search with initial load support
  const { data: searchResults, isLoading: isSearching } =
    trpc.admin.coverageArea.searchForAutocomplete.useQuery(
      { searchTerm: citySearchTerm },
      {
        enabled: shouldQueryServer,
        placeholderData: keepPreviousData,
      },
    );

  // Cache to store city data when selected or searched
  const [cityDataCache, setCityDataCache] = useState<Map<string, { city: string; state: string }>>(
    new Map(),
  );

  const [formData, setFormData] = useState({
    city: '',
    state: '',
    feeInDollars: '0.00',
    archived: false,
  });

  const [errors, setErrors] = useState<Record<string, string>>({});
  const [cityWarning, setCityWarning] = useState<string>('');

  // Update cache when search results come in
  useEffect(() => {
    if (searchResults) {
      setCityDataCache((prevCache) => {
        const newCache = new Map(prevCache);
        searchResults.cities.forEach((city) => {
          newCache.set(city.city, city);
        });
        return newCache;
      });
    }
  }, [searchResults]);

  // Transform search results to autocomplete options
  const cityOptions: AutocompleteOption[] = useMemo(() => {
    // Start with cached cities (important for editing existing city fees)
    const cachedOptions: AutocompleteOption[] = Array.from(cityDataCache.values()).map((city) => ({
      value: city.city,
      label: city.city,
      description: city.state,
    }));

    // If we have search results, merge them with cached data
    if (searchResults) {
      // Backend already limits initial load to 10 cities via aggregation pipeline
      const searchOptions = searchResults.cities.map((city) => ({
        value: city.city,
        label: city.city,
        description: city.state,
      }));

      // Combine cached and search results, removing duplicates
      const combinedMap = new Map<string, AutocompleteOption>();
      [...cachedOptions, ...searchOptions].forEach((opt) => {
        combinedMap.set(opt.value, opt);
      });

      return Array.from(combinedMap.values());
    }

    // If no search results yet, return just cached options (for editing)
    return cachedOptions;
  }, [searchResults, cityDataCache]);

  // Initialize form data when city fee changes or sheet opens
  useEffect(() => {
    if (open) {
      if (cityFee) {
        // Editing existing city fee
        setFormData({
          city: cityFee.city,
          state: cityFee.state,
          feeInDollars: (cityFee.feeInCents / 100).toFixed(2),
          archived: cityFee.archived,
        });
        // Populate cache with existing city data so it displays in autocomplete
        setCityDataCache(new Map([[cityFee.city, { city: cityFee.city, state: cityFee.state }]]));
      } else {
        // Creating new city fee
        setFormData({
          city: '',
          state: '',
          feeInDollars: '0.00',
          archived: false,
        });
        // Clear cache for new city fee
        setCityDataCache(new Map());
      }
      setErrors({});
      setCityWarning('');
    }
  }, [open, cityFee]);

  // Cleanup: Reset search state when sheet closes
  useEffect(() => {
    if (!open) {
      setCitySearchTerm('');
      setCityPopoverOpen(false);
    }
  }, [open]);

  // Validate city against coverage areas
  const validateCityAgainstCoverage = (city: string, state: string): boolean => {
    if (!city.trim() || !state.trim()) return true; // Skip if empty

    // Check if city exists in cache (was previously searched/selected)
    const cityData = cityDataCache.get(city);
    if (cityData && cityData.state === state.toUpperCase()) {
      setCityWarning('');
      return true;
    }

    // If not in cache, show warning
    setCityWarning(
      'This city is not in your coverage areas. Please add it to Coverage Areas first.',
    );
    return false;
  };

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.city.trim()) {
      newErrors.city = 'City is required';
    }

    if (!formData.state.trim()) {
      newErrors.state = 'State is required';
    } else if (formData.state.length !== 2) {
      newErrors.state = 'State must be 2 characters';
    }

    const fee = parseFloat(formData.feeInDollars);
    if (isNaN(fee) || fee < 0) {
      newErrors.feeInDollars = 'Fee must be a positive number';
    }

    // Check if city exists in coverage areas
    const cityIsValid = validateCityAgainstCoverage(formData.city, formData.state);
    if (!cityIsValid) {
      newErrors.city = 'City must exist in coverage areas';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0 && cityIsValid;
  };

  const handleSubmit = () => {
    if (!validateForm()) {
      return;
    }

    const feeInCents = Math.round(parseFloat(formData.feeInDollars) * 100);

    if (cityFee?._id) {
      // Update existing city fee
      updateFee(cityFee._id, {
        city: formData.city.trim(),
        state: formData.state.trim().toUpperCase(),
        feeInCents,
        archived: formData.archived,
      });
    } else {
      // Create new city fee
      createFee({
        city: formData.city.trim(),
        state: formData.state.trim().toUpperCase(),
        feeInCents,
        archived: formData.archived,
      });
    }

    onOpenChange(false);
  };

  const handleClose = () => {
    onOpenChange(false);
  };

  const isLoading = isCreating || isUpdating;

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="w-full sm:max-w-lg h-full p-0">
        <div className="grid h-full grid-rows-[auto_1fr_auto]">
          <SheetHeader className="border-b bg-white px-6 py-4">
            <SheetTitle>{cityFee ? 'Edit City Fee' : 'Create City Fee'}</SheetTitle>
          </SheetHeader>

          <div className="overflow-y-auto px-6 py-6">
            <div className="space-y-6">
              {/* City */}
              <div className="space-y-2">
                <Label htmlFor="city">City</Label>
                <Autocomplete
                  options={cityOptions}
                  value={formData.city}
                  onValueChange={(value) => {
                    // Auto-populate state from cache or search results
                    const cityData =
                      cityDataCache.get(value) ||
                      searchResults?.cities.find((c) => c.city === value);

                    // Single atomic update with both city and state
                    if (cityData && value) {
                      setFormData((prev) => ({
                        ...prev,
                        city: value,
                        state: cityData.state,
                      }));
                    } else {
                      setFormData((prev) => ({ ...prev, city: value }));
                    }

                    // Clear errors and warnings
                    if (errors.city) setErrors({ ...errors, city: '' });
                    setCityWarning('');
                  }}
                  onSearchChange={(searchTerm) => {
                    setCitySearchTerm(searchTerm);
                  }}
                  onPopoverOpenChange={(isOpen) => {
                    setCityPopoverOpen(isOpen);
                  }}
                  isLoading={isSearching}
                  minSearchLength={2}
                  searchPlaceholder="Type to search cities..."
                  placeholder="Search for a city"
                  emptyMessage="No cities found in coverage areas"
                  disabled={false}
                  showClearButton={true}
                  className="w-full text-muted bg-white"
                />
                {errors.city && <p className="text-sm text-red-500">{errors.city}</p>}
                {cityWarning && !errors.city && (
                  <div className="flex items-start gap-2 p-3 bg-yellow-50 border border-yellow-200 rounded-md">
                    <AlertCircle className="h-4 w-4 text-yellow-600 mt-0.5 flex-shrink-0" />
                    <p className="text-sm text-yellow-800">{cityWarning}</p>
                  </div>
                )}
              </div>

              {/* State */}
              <div className="space-y-2">
                <Label htmlFor="state">State (2-letter code)</Label>
                <Input
                  id="state"
                  value={formData.state}
                  onChange={(e) => {
                    const value = e.target.value.toUpperCase().slice(0, 2);
                    setFormData({ ...formData, state: value });
                    if (errors.state) setErrors({ ...errors, state: '' });

                    // Validate city against coverage if state changes and city is filled
                    if (value.length === 2 && formData.city) {
                      validateCityAgainstCoverage(formData.city, value);
                    }
                  }}
                  onBlur={() => {
                    // Validate on blur
                    if (formData.city) {
                      validateCityAgainstCoverage(formData.city, formData.state);
                    }
                  }}
                  placeholder="CO"
                  maxLength={2}
                  minLength={2}
                  className={errors.state ? 'border-red-500' : ''}
                  disabled={true}
                />
                {errors.state && <p className="text-sm text-red-500">{errors.state}</p>}
                <p className="text-sm text-gray-500">
                  Enter the 2-letter state abbreviation (e.g., CO, CA, NY)
                </p>
              </div>

              {/* Fee */}
              <div className="space-y-2">
                <Label htmlFor="fee">Fee</Label>
                <div className="relative">
                  <NumericFormat
                    customInput={Input}
                    prefix="$"
                    decimalScale={2}
                    fixedDecimalScale={true}
                    id="fee"
                    value={formData.feeInDollars}
                    onValueChange={(values) =>
                      setFormData({ ...formData, feeInDollars: values.value })
                    }
                    placeholder="0.00"
                    className={`${errors.feeInDollars ? 'border-red-500' : ''}`}
                  />
                </div>
                {errors.feeInDollars && (
                  <p className="text-sm text-red-500">{errors.feeInDollars}</p>
                )}
                <p className="text-sm text-gray-500">Fee amount in dollars and cents</p>
              </div>

              {/* Archived toggle */}
              {cityFee && (
                <div className="flex items-center space-x-2">
                  <Switch
                    id="archived"
                    checked={formData.archived}
                    onCheckedChange={(checked) => setFormData({ ...formData, archived: checked })}
                  />
                  <Label htmlFor="archived" className="cursor-pointer">
                    Archived
                  </Label>
                </div>
              )}
            </div>
          </div>

          <SheetFooter className="border-t bg-gray-50 px-6 py-4">
            <div className="flex justify-end w-full gap-2">
              <Button variant="outline" onClick={handleClose} disabled={isLoading}>
                Cancel
              </Button>
              <Button onClick={handleSubmit} disabled={isLoading}>
                {isLoading ? 'Saving...' : cityFee ? 'Update' : 'Create'}
              </Button>
            </div>
          </SheetFooter>
        </div>
      </SheetContent>
    </Sheet>
  );
};
